/**
 * Pure functions for computing a partial-return/refund total, including
 * fair proration of the original invoice-level discount across only the
 * lines actually being returned.
 *
 * Pipeline: computeReturnLine (per line) -> aggregateReturnLines (sum) ->
 * prorateInvoiceDiscount (invoice-level share) -> computeReturnRefund (orchestrator).
 */

export type ItemDiscount =
  | { kind: "PerItem"; amount: number }
  | { kind: "Percentage"; percent: number; maxAmount?: number };

export type InvoiceDiscount =
  | { kind: "Fixed"; amount: number }
  | { kind: "Percentage"; percent: number };

export interface ReturnLineInput {
  itemId: string | number;
  returnQty: number;
  price: number;
  taxRate: number;
  itemDiscount?: ItemDiscount | null;
}

export interface ComputedReturnLine {
  itemId: string | number;
  itemGross: number;
  itemDiscount: number;
  itemNet: number;
  tax: number;
  itemTotal: number;
}

export interface ReturnAggregate {
  lines: ComputedReturnLine[];
  returnGrossSubtotal: number;
  returnNetSubtotal: number;
  returnTaxTotal: number;
  returnItemTotalSum: number;
}

/**
 * Item-level discount for one returned line.
 * PerItem is a fixed amount per unit, scaled by returnQty; Percentage is a
 * share of that line's own gross (qty * price), optionally capped by
 * maxAmount. Result never exceeds the line's own gross.
 */
export function computeItemDiscount(
  itemGross: number,
  returnQty: number,
  discount: ItemDiscount | null | undefined,
): number {
  if (!discount || itemGross <= 0) return 0;

  let amount: number;
  if (discount.kind === "PerItem") {
    amount = discount.amount * returnQty;
  } else {
    amount = itemGross * (discount.percent / 100);
    if (typeof discount.maxAmount === "number" && discount.maxAmount >= 0) {
      amount = Math.min(amount, discount.maxAmount);
    }
  }

  return Math.max(0, Math.min(amount, itemGross));
}

/** Computes gross/discount/net/tax/total for a single returned line. */
export function computeReturnLine(input: ReturnLineInput): ComputedReturnLine {
  const { itemId, returnQty, price, taxRate, itemDiscount } = input;
  const itemGross = returnQty * price;
  const itemDiscountAmount = computeItemDiscount(itemGross, returnQty, itemDiscount);
  const itemNet = itemGross - itemDiscountAmount;
  const tax = itemNet * (taxRate / 100);
  const itemTotal = itemNet + tax;

  return {
    itemId,
    itemGross,
    itemDiscount: itemDiscountAmount,
    itemNet,
    tax,
    itemTotal,
  };
}

/** Sums computed lines into the aggregate subtotals used for invoice-discount proration. */
export function aggregateReturnLines(lines: ComputedReturnLine[]): ReturnAggregate {
  return lines.reduce<ReturnAggregate>(
    (acc, line) => ({
      lines: [...acc.lines, line],
      returnGrossSubtotal: acc.returnGrossSubtotal + line.itemGross,
      returnNetSubtotal: acc.returnNetSubtotal + line.itemNet,
      returnTaxTotal: acc.returnTaxTotal + line.tax,
      returnItemTotalSum: acc.returnItemTotalSum + line.itemTotal,
    }),
    { lines: [], returnGrossSubtotal: 0, returnNetSubtotal: 0, returnTaxTotal: 0, returnItemTotalSum: 0 },
  );
}

/**
 * Prorates the invoice-level discount onto the returned share.
 *
 * Fixed: proportion = returnGrossSubtotal / invoiceGrossSubtotal — both sides
 * MUST be pre-item-discount ("gross") subtotals. Mixing a post-item-discount
 * ("net") value into either side of this ratio understates or overstates the
 * refund whenever any line carries its own item-level discount.
 *
 * Percentage: reapplied directly to returnNetSubtotal — no ratio needed,
 * since a percentage already scales proportionally to whatever base you feed it.
 */
export function prorateInvoiceDiscount(
  invoiceDiscount: InvoiceDiscount | null | undefined,
  aggregate: Pick<ReturnAggregate, "returnGrossSubtotal" | "returnNetSubtotal">,
  invoiceGrossSubtotal: number,
): number {
  if (!invoiceDiscount) return 0;

  if (invoiceDiscount.kind === "Fixed") {
    if (invoiceGrossSubtotal <= 0) return 0;
    const proportion = aggregate.returnGrossSubtotal / invoiceGrossSubtotal;
    const prorated = invoiceDiscount.amount * proportion;
    return Math.max(0, Math.min(prorated, invoiceDiscount.amount));
  }

  return Math.max(0, aggregate.returnNetSubtotal * (invoiceDiscount.percent / 100));
}

export interface ReturnRefundResult extends ReturnAggregate {
  invoiceDiscountForReturn: number;
  refundTotal: number;
}

/**
 * Orchestrates the full pipeline for a partial return: per-line calc,
 * aggregation, invoice-discount proration, and final refund total.
 *
 * Invoice discount is applied after tax (reduces the refund, not the taxable
 * base) — confirm this matches the business rule; swap to a pre-tax
 * application if invoice discount should instead reduce itemNet before tax.
 */
export function computeReturnRefund(
  returnLines: ReturnLineInput[],
  invoiceDiscount: InvoiceDiscount | null | undefined,
  invoiceGrossSubtotal: number,
): ReturnRefundResult {
  const computedLines = returnLines.map(computeReturnLine);
  const aggregate = aggregateReturnLines(computedLines);
  const invoiceDiscountForReturn = prorateInvoiceDiscount(
    invoiceDiscount,
    aggregate,
    invoiceGrossSubtotal,
  );
  const refundTotal = Math.max(0, aggregate.returnItemTotalSum - invoiceDiscountForReturn);

  return {
    ...aggregate,
    invoiceDiscountForReturn,
    refundTotal,
  };
}
